/**
 * ============================================================================
 * SAVE PRESET MODAL — save the current filter combination for reuse.
 * ============================================================================
 *
 *
 * WHY: users re-select the same 3-4 filter combination manually every WBR.
 *
 * PERSISTENCE: localStorage keyed by user alias —
 *   key:   `wbr-presets:${userAlias}`
 *   value: JSON.stringify(FilterPreset[])   (FilterPreset in src/types)
 *
 * IMPLEMENTATION PLAN:
 * 1. Save flow: FilterBar's "Save Preset" opens this modal → user names it →
 *    onSave snapshots current filters from the store into localStorage.
 * 2. Load flow (TODO follow-up): a preset dropdown in FilterBar listing saved
 *    presets; selecting one dispatches filtersReplaced(preset.filters).
 * 3. Handle: duplicate names (overwrite with confirm), quota errors
 *    (try/catch around setItem), corrupt JSON (fall back to []).
 * 4. MIGRATION NOTE: if presets should roam across browsers later, swap the
 *    storage layer for a backend endpoint — keep reads/writes behind a small
 *    presetStorage module so the swap is one file.
 */

import React, { useState } from 'react';
import Modal from '@amzn/awsui-components-core/modal';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Box from '@amzn/awsui-components-core/box';

interface SavePresetModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Parent snapshots filters from the store and writes to localStorage. */
  onSave: (name: string) => void;
}

export const SavePresetModal: React.FC<SavePresetModalProps> = ({ visible, onDismiss, onSave }) => {
  const [name, setName] = useState('');

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Save filter preset"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!name.trim()}
              onClick={() => {
                onSave(name.trim());
                setName('');
              }}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField label="Preset name" description="e.g. 'Monday WBR — Nova prod only'">
        <Input value={name} onChange={({ detail }) => setName(detail.value)} />
      </FormField>
    </Modal>
  );
};
